// Audit workspace data layer. audit_logs has no severity/actor-label columns, so
// severity + actor kind are DERIVED here (a transparent classification of the
// real action string — not fabricated data).
import { apiRequest } from '@/lib/supabase';

export interface AuditLogRow {
  id: string;
  user_id: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  changes?: Record<string, unknown> | null;
  ip_address: string | null;
  api_key_id: string | null;
  created_at: string;
}

export interface LicenseEventRow {
  id: string;
  license_id: string;
  event_type: string;
  event_data?: Record<string, unknown> | null;
  user_id?: string | null;
  created_at: string;
  license?: { license_id?: string; organization?: { id?: string; name?: string } | null } | null;
}

export interface ValidationLogRow {
  id: string;
  timestamp: string;
  license_id: string;
  license_key: string | null;
  org_name: string | null;
  org_id?: string | null;
  instance_id: string | null;
  ip_address: string;
  success: boolean;
  error_code: string | null;
}

export interface ValidationRollup {
  days: number;
  totals: { valid: number; denied: number; accounts: number; denial_rate: number };
  series: Array<{ day: string; valid: number; denied: number }>;
  accounts: Array<{ organization_id: string | null; org_name: string | null; valid: number; denied: number; distinct_ips: number; last_seen: string | null }>;
}

export interface OverageEvidenceRow {
  org: string | null;
  organization_id?: string | null;
  license_id: string | null;
  metric: string;
  peak: number;
  pool: number | null;
  overage: number;
  billed: number;
  billing_state: string;
  scope?: string;
}

export type AuditSeverity = 'critical' | 'high' | 'medium' | 'info';
const SEV_RANK: Record<AuditSeverity, number> = { critical: 3, high: 2, medium: 1, info: 0 };

export function severityOf(action: string): AuditSeverity {
  const a = (action || '').toLowerCase();
  if (a.includes('revoke') || a.includes('delete') || a.includes('denied') || a.includes('reject')) return 'critical';
  if (a.includes('create') || a.includes('suspend') || a.includes('key') || a.includes('invite')) return 'high';
  if (a.includes('update') || a.includes('role') || a.includes('renew') || a.includes('reinstate')) return 'medium';
  return 'info';
}
export function severityRank(s: AuditSeverity): number { return SEV_RANK[s]; }

export type ActorKind = 'admin' | 'api_key' | 'system';
export function actorKindOf(row: AuditLogRow): ActorKind {
  if (row.api_key_id) return 'api_key';
  if (row.user_id) return 'admin';
  return 'system';
}
export function actorLabelOf(row: AuditLogRow): string {
  if (row.api_key_id) return 'API key';
  if (row.user_id) return 'Admin user';
  return 'System';
}
export function isAccessChange(action: string): boolean {
  return /role|api_key|key\.|invite|user\.|member/.test((action || '').toLowerCase());
}

export const getAuditLogs = (limit = 200) =>
  apiRequest<{ data: AuditLogRow[] }>(`/users/audit-logs?limit=${limit}`).then((r) => r.data || []);
export const getLicenseEvents = (limit = 100) =>
  apiRequest<LicenseEventRow[]>(`/analytics/license-events?limit=${limit}`);
export const getValidationsLog = (opts?: { denied?: boolean; limit?: number }) =>
  apiRequest<ValidationLogRow[]>(
    `/analytics/validations-log?limit=${opts?.limit ?? 100}${opts?.denied ? '&result=denied' : ''}`,
  );
export const getOverageEvidence = () => apiRequest<OverageEvidenceRow[]>('/analytics/overage-evidence');
export const getValidationsRollup = (days = 30) => apiRequest<ValidationRollup>(`/analytics/validations-rollup?days=${days}`);
